import { useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { useSetRecoilState } from 'recoil';

import { useAuthContext } from '../../AuthProvider';
import { useSuggestMeal, useSuggestSleep } from '../../../mutations';
import { debounce } from 'lodash';
import { User } from '@demind-inc/core';
import { useAssistantPref } from '../../../hooks/useAssistantPref';
import { isMealSuggestingAtom } from '../../../recoil/assistantChat.atom';
import { trackEventMixpanel } from '../../../../utils';

const STORAGE_KEY = '@auto_suggestions_date';

export const useAutoSuggestions = () => {
  const { user } = useAuthContext();
  const setIsMealSuggesting = useSetRecoilState(isMealSuggestingAtom);

  const isInitialized = useRef(false);
  const { defaultCalendar: mainCalendar, mealPref, isPreferenceLoading } = useAssistantPref();

  const { suggestSleep } = useSuggestSleep();
  const { suggestMeal, isPending: isMealSuggesting } = useSuggestMeal();

  useEffect(() => {
    setIsMealSuggesting(isMealSuggesting);
  }, [isMealSuggesting, setIsMealSuggesting]);

  const getStoredDate = useCallback((): string | null => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error reading auto suggestions date:', error);
      return null;
    }
  }, []);

  const setStoredDate = useCallback((date: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, date);
    } catch (error) {
      console.error('Error writing auto suggestions date:', error);
    }
  }, []);

  const runSleepSuggestion = useCallback(
    debounce(async (targetUser: User, targetCalendarId?: string) => {
      if (!targetUser.userId || !targetUser.metricId || !targetUser.chatId || !targetCalendarId) {
        return;
      }

      try {
        const today = dayjs().format('YYYY-MM-DD');
        const res = await suggestSleep({
          userId: targetUser.userId,
          metricId: targetUser.metricId!,
          dateToBeSuggested: today,
          chatId: targetUser.chatId!,
          calendarId: targetCalendarId,
        });

        // Store today's date
        setStoredDate(today);
        isInitialized.current = true;

        return res;
      } catch (error) {
        console.error('Error running sleep suggestion:', error);
      }
    }, 8000),
    []
  );

  const runMealSuggestion = useCallback(
    debounce(async (targetUser: User, targetCalendarId?: string, skip?: boolean) => {
      if (
        !targetUser.userId ||
        !targetUser.metricId ||
        !targetUser.chatId ||
        !targetUser.routineId ||
        !targetCalendarId ||
        skip
      ) {
        return;
      }

      try {
        const today = dayjs().format('YYYY-MM-DD');
        const res = await suggestMeal({
          routineId: targetUser.routineId!,
          chatId: targetUser.chatId!,
          userId: targetUser.userId,
          calendarId: targetCalendarId,
          metricId: targetUser.metricId!,
          dateToBeSuggested: today,
        });

        // Store today's date
        setStoredDate(today);
        isInitialized.current = true;

        return res;
      } catch (error) {
        console.error('Error running meal suggestion:', error);
      }
    }, 8000),
    []
  );

  const runAutoSuggestions = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (force) {
        trackEventMixpanel('rerun_auto_suggestion', { source: 'assistant_chat_auto' });
      }
      if ((isInitialized.current || isPreferenceLoading) && !force) {
        return;
      }

      const today = dayjs().format('YYYY-MM-DD');
      const storedDate = getStoredDate();

      // Check if we've already run suggestions today
      if (storedDate === today && !force) {
        isInitialized.current = true;
        return;
      }

      // Run suggestions in parallel, skipping meal when auto schedule is disabled
      const suggestionPromises: Promise<any>[] = [
        runSleepSuggestion(user, mainCalendar?.calendarId),
      ];
      suggestionPromises.push(
        runMealSuggestion(user, mainCalendar?.calendarId, mealPref.disableAutoSchedule)
      );

      await Promise.all(suggestionPromises);
    },
    [user, mainCalendar, mealPref.disableAutoSchedule, isPreferenceLoading]
  );

  const clearStoredDate = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing auto suggestions date:', error);
    }
  }, []);

  return {
    runAutoSuggestions,
    clearStoredDate,
    isInitialized: isInitialized.current,
  };
};
